import {
	ContentCard,
	RenderComponentOrLoading,
	StylovyzeTableFooter,
	useIsFeatureEnabled,
} from '@innovyze/stylovyze';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { SensorConfigurationsTable } from '@Components';
import SensorConfigurationsControlsBar from '@Components/SensorConfigurationsControlsBar';
import { useConfigurationsPagination, useConfigurationsSearch } from '@Hooks';
import { getValue, useIsConfigurationsInitialized } from '@Selectors';
import MuixSensorConfigurationsTable from '@Components/SensorConfigurationsTable/MuixSensorConfigurationsTable';
import { FLAGS } from '@Types';
import {
	pollSensorTroubleshootingStop,
	resetConfigurationsStore,
	systemConnectionTestAsyncPollStop,
} from '@Actions';

const SensorConfigurations = () => {
	// selectors
	const [pagination, fetchConfigurationsWithCriteria] =
		useConfigurationsPagination();
	const { searchValue, sensorTypes, dataSourceTypes } =
		useConfigurationsSearch();
	const {
		configurations: { status, physicalOrVirtual },
	} = useSelector(getValue);
	const dispatch = useDispatch();

	const isMuixConfigurationsTableEnabled = useIsFeatureEnabled(
		FLAGS.ENABLE_MUIX_TABLE_FOR_SENSOR_CONFIGURATION,
	);

	const isConfigurationsInitialized = useIsConfigurationsInitialized();

	useEffect(() => {
		dispatch(pollSensorTroubleshootingStop());
		dispatch(systemConnectionTestAsyncPollStop());
		fetchConfigurationsWithCriteria(
			pagination.current || 1,
			searchValue ?? '',
			sensorTypes,
			dataSourceTypes,
			physicalOrVirtual,
			true,
		);

		return () => {
			dispatch(resetConfigurationsStore());
		};
	}, []);

	const showEmptyTable =
		(!pagination || !pagination.total) &&
		!searchValue &&
		status !== 'pending';

	const handleOnChangeConfigurationsPage = (
		_: unknown,
		page: number,
	): void => {
		fetchConfigurationsWithCriteria(page, searchValue, sensorTypes);
	};

	const handleOnChangeMuixConfigurationsPage = (
		page: number,
		pageSize: number,
	): void => {
		fetchConfigurationsWithCriteria(
			page,
			searchValue,
			sensorTypes,
			dataSourceTypes,
			physicalOrVirtual,
			true,
			pageSize,
		);
	};

	return (
		<RenderComponentOrLoading loading={!isConfigurationsInitialized}>
			{isMuixConfigurationsTableEnabled ? (
				<MuixSensorConfigurationsTable
					pagination={{
						onChange: handleOnChangeMuixConfigurationsPage,
						currentPage: pagination.current,
						totalNumberOfRows: pagination.total,
					}}
					dataCy="sensor configurations grid"
				/>
			) : (
				<ContentCard
					minHeight={'600px'}
					applyBodyPadding={false}
					header={<SensorConfigurationsControlsBar />}
					footer={
						!showEmptyTable ? (
							<StylovyzeTableFooter
								pagination={{
									onChange: handleOnChangeConfigurationsPage,
									currentPage: pagination.current,
									totalNumberOfPages: pagination.pages,
									dataCy: 'sensor configuration pagination',
								}}
							/>
						) : undefined
					}>
					<SensorConfigurationsTable />
				</ContentCard>
			)}
		</RenderComponentOrLoading>
	);
};

export default SensorConfigurations;
